<template>
  <div>
    <el-container
      id="keyreply-chat-window"
      v-loading="loading || uploading"
      :class="{
        tall: tall,
        wide: settings.window === 'wide',
        full: settings.window === 'full',
        iossdk: iOSSDK,
        [`right-${chatWindowRight}`]: isRtlModeEnabled,
      }"
      :style="chatWindowCssVars"
      :element-loading-text="loadingText"
      ref="keyReplyChatWindow"
    >
      <!--  Keyreply Menu Header Modal  -->
      <transition name="fade">
        <div
          id="keyreply-header-menu"
          v-if="toShowHeaderMenu && isUiBetaEnabled"
          @click.self="closeHeaderMenu"
        >
          <div class="keyreply-header-menu-container">
            <div class="keyreply-header-menu-section" v-if="isShowWidgetSizeSection">
              <span class="keyreply-header-menu-section-tittle">Widget Size</span>
              <div class="keyreply-header-menu-button-group">
                <template v-if="!$device.Mobile && !tallViewSettings">
                  <div
                    v-if="!tall"
                    class="keyreply-header-button-item"
                    @click="handleChangeWidgetSize(true)"
                  >
                    <div class="keyreply-header-button">
                      <el-icon><FullScreen /></el-icon>
                    </div>
                    <span>Expand</span>
                  </div>
                  <div
                    v-else
                    class="keyreply-header-button-item"
                    @click="handleChangeWidgetSize(false)"
                  >
                    <div class="keyreply-header-button">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 3V4C8 5.88562 8 6.82843 7.41421 7.41421C6.82843 8 5.88562 8 4 8H3"
                          :stroke="store.settings.widgetIconTextColor"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M16 3V4C16 5.88562 16 6.82843 16.5858 7.41421C17.1716 8 18.1144 8 20 8H21"
                          :stroke="store.settings.widgetIconTextColor"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M8 21V20C8 18.1144 8 17.1716 7.41421 16.5858C6.82843 16 5.88562 16 4 16H3"
                          :stroke="store.settings.widgetIconTextColor"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M16 21V20C16 18.1144 16 17.1716 16.5858 16.5858C17.1716 16 18.1144 16 20 16H21"
                          :stroke="store.settings.widgetIconTextColor"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span>Default</span>
                  </div>
                </template>
              </div>
            </div>
            <div class="keyreply-header-menu-section">
              <span class="keyreply-header-menu-section-tittle">Chat History</span>
              <div class="keyreply-header-menu-button-group">
                <div class="keyreply-header-button-item" @click="clearCache">
                  <div class="keyreply-header-button">
                    <el-icon><Delete /></el-icon>
                  </div>
                  <span>Clear</span>
                </div>
                <div
                  class="keyreply-header-button-item"
                  @click="downloadHistory"
                  v-if="settings.showHistoryDownload"
                >
                  <div class="keyreply-header-button">
                    <el-icon><Download /></el-icon>
                  </div>
                  <span>Download</span>
                </div>
              </div>
            </div>
            <div class="keyreply-header-powered">
              <span>Powered by <span style="color: #a6b5e6">RisingPoint</span></span>
            </div>
          </div>
        </div>
      </transition>
      <!-- End Kereply Header Modal -->
      <el-header
        id="keyreply-panel-header"
        :class="headerClass"
        @mousedown="
          $emit('handleGrabElement', { event: $event, elementId: 'keyreply-chat-window' })
        "
        ref="keyreply-panel-header"
      >
        <template v-if="!isMobileModeDefault">
          <span v-if="settings.logo">
            <img
              :src="settings.logo"
              :class="settings.largeLogo ? 'keyreply-chat-logo-large' : 'keyreply-chat-logo'"
            />
          </span>
          <el-tabs
            v-if="settings.tabs && Array.isArray(settings.tabs) && settings.tabs.length > 0"
            ref="tabs"
            :stretch="true"
            :value="settings.tabs[0].url"
            :class="settings.window === 'wide' ? 'inline-block w-[80%]' : 'inline-block w-[70%]'"
            @tab-click="handleTabClick"
          >
            <el-tab-pane v-for="(tab, index) in settings.tabs" :key="index" :name="tab.url">
              <template #label><span :style="getTabStyleCssVars(tab.style)" class="tab-label-styled">{{ tab.title }}</span></template>
            </el-tab-pane>
          </el-tabs>

          <div
            v-else
            :class="settings.largeLogo ? 'keyreply-chat-title-large' : 'keyreply-chat-title'"
          >
            {{ settings.title }}
          </div>

          <div class="keyreply-header-button" v-if="!isUiBetaEnabled">
            <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
            <el-button
              v-if="
                !sessionEnded &&
                sessionButtonReady &&
                (!settings.disableUserResolve || !settings.liveChatStatus)
              "
              class="mr-3"
              round
              plain
              type="success"
              :loading="endButtonLoading"
              @click="endSession"
              >{{ settings.liveChatStatus ? "End Live Chat" : "End Chat" }}</el-button
            >
            <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
            <el-button
              v-else-if="sessionButtonReady"
              class="mr-3"
              round
              plain
              type="primary"
              :loading="endButtonLoading"
              @click="startSession"
              >Start New Chat</el-button
            >
            <div
              v-if="!$device.Mobile && !tallViewSettings"
              class="mr-3"
              @click="tall = !tall"
            >
              <el-icon><component :is="tall ? BottomLeft : FullScreen" /></el-icon>
            </div>

            <el-dropdown
              :class="{
                'mr-[36px]': isMobileMode,
                'mr-[12px]': !isMobileMode,
              }"
              size="small"
              placement="bottom-end"
              :trigger="triggerOption"
              @command="handleCommand"
            >
              <span class="keyreply-dropdown-icon">
                <el-icon><More /></el-icon>
              </span>
              <template #dropdown><el-dropdown-menu>
                <el-dropdown-item
                  command="endChat"
                  v-if="postChatFormEnabled && !endSessionButtonEnabled"
                  >End Chat</el-dropdown-item
                >
                <el-dropdown-item command="clearCache">{{
                  `Clear ${endSessionButtonEnabled ? "Cache" : "History"}`
                }}</el-dropdown-item>
                <el-dropdown-item v-if="showHistoryDownload" command="downloadHistory"
                  >Download History</el-dropdown-item
                >
                <el-dropdown-item v-if="showClearCloneState" command="clearCloneState"
                  >Clear Clone State</el-dropdown-item
                >
              </el-dropdown-menu></template>
            </el-dropdown>

            <el-tooltip
              v-if="!isMobileMode"
              v-touch:tap="tapHandler"
              :hide-after="1000"
              content="Minimize"
              placement="bottom"
            >
              <el-icon @click="closeWindow"><ArrowDown /></el-icon>
            </el-tooltip>
          </div>
          <div class="keyreply-header-button" v-else>
            <!-- New header button -->
            <div class="flex">
              <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
              <el-button
                v-if="
                  !sessionEnded &&
                  sessionButtonReady &&
                  (!settings.disableUserResolve || !settings.liveChatStatus)
                "
                class="mr-3"
                round
                plain
                type="success"
                :loading="endButtonLoading"
                @click="endSession"
                >{{ settings.liveChatStatus ? "End Live Chat" : "End Chat" }}</el-button
              >
              <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
              <el-button
                v-else-if="sessionButtonReady"
                class="mr-3"
                round
                plain
                type="primary"
                :loading="endButtonLoading"
                @click="startSession"
                >Start New Chat</el-button
              >

              <div
                class="flex items-center mx-[10px] my-0 text-2xl font-bolder"
                @click="closeWindow"
              >
                <el-tooltip :hide-after="1000" content="Minimize widget" placement="bottom">
                  <div class="w-5 h-[3px] rounded-[5px] keyreply-hamburger-line"></div>
                </el-tooltip>
              </div>

              <el-tooltip
                :hide-after="1000"
                content="Menu"
                placement="bottom"
              >
                <svg
                  @click="openHeaderMenu"
                  class="cursor-pointer"
                  width="32"
                  height="100%"
                  viewBox="0 0 25 25"
                  xmlns="http://www.w3.org/2000/svg"
                  :fill="headerModalIconColor"
                >
                  <path
                    d="M4 4h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 10h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 16h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4z"
                  />
                </svg>
              </el-tooltip>
            </div>
          </div>
        </template>
        <el-collapse-transition>
          <div v-if="!isConnected">
            <!-- <div v-if="$store.state.socketConnectionStatus"> -->
            <div
              class="keyreply-disconnected-banner"
              :class="[
                bannerClass,
                {
                  'transition-box': isUiBetaEnabled,
                },
              ]"
            >
              <span>
                <el-icon><WarningFilled /></el-icon>
                {{ isUiBetaEnabled ? "Please check your internet connection…" : "Disconnected." }}
              </span>
            </div>
          </div>
          <!-- <div v-else-if="settings.typingIndicator.status && isUiBetaEnabled">
            <div class="keyreply-typing-banner transition-box" :class="bannerClass">
              <span>{{ settings.name }} is typing...</span>
            </div>
          </div> -->
          <div v-else-if="settings.liveChatStatus">
            <div
              v-if="settings.largeLogo"
              class="keyreply-livechat-banner-large"
              :class="bannerClass"
            >
              <span> <el-icon><Service /></el-icon> Live Chat in progress </span>
              <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
              <el-button
                v-if="!endSessionButtonEnabled && !settings.disableUserResolve"
                class="keyreply-livechat-end-button"
                type="primary"
                round
                plain
                @click="endLiveChat()"
                >End Live Chat</el-button
              >
            </div>
            <div v-else class="keyreply-livechat-banner" :class="bannerClass">
              <span> <el-icon><Service /></el-icon> Live Chat in progress </span>
              <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
              <el-button
                v-if="!endSessionButtonEnabled && !settings.disableUserResolve"
                class="keyreply-livechat-end-button"
                type="primary"
                round
                plain
                @click="endLiveChat()"
                >End Live Chat</el-button
              >
            </div>
          </div>
          <div
            v-else-if="isMaintenanceBannerEnabled && isShowMaintenanceBanner"
            :class="bannerClass"
          >
            <div class="keyreply-maintenance-banner">
              <span
                id="keyreply-maintenance-banner-text"
                v-html="markHTML(settings.maintenanceBanner?.message || '', settings.buttonOutlineColor || '#409eff')"
              ></span>
              <!-- close icon -->
              <el-button
                class="keyreply-maintenance-banner-close"
                link
                @click="closeMaintenanceBanner"
              >
                <el-icon><Close /></el-icon>
              </el-button>
            </div>
          </div>
        </el-collapse-transition>
        <el-collapse-transition>
          <div v-if="showingNotifBanner" @click="requestNotificationPermission">
            <div class="keyreply-notification-banner">
              <span id="keyreply-notification-banner-text"
                >Enable Notification Permission in your browser</span
              >
            </div>
          </div>
        </el-collapse-transition>
        <div :class="floatingButtonClass" v-if="isMobileModeDefault">
          <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
          <el-button
            v-if="
              sessionButtonReady &&
              settings.liveChatStatus &&
              (!settings.disableUserResolve || !settings.liveChatStatus)
            "
            class="mr-3"
            round
            plain
            type="success"
            :loading="endButtonLoading"
            @click="endSession"
          >
            {{ settings.liveChatStatus ? "End Live Chat" : "End Chat" }}
          </el-button>
          <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
          <el-button
            v-if="sessionEnded && sessionButtonReady"
            class="mr-3"
            round
            plain
            type="primary"
            :loading="endButtonLoading"
            @click="startSession"
          >
            Start New Chat
          </el-button>
          <el-dropdown
            v-if="!isUiBetaEnabled"
            class="mr-3"
            size="small"
            placement="bottom-end"
            :trigger="triggerOption"
            @command="handleCommand"
          >
            <el-button
              :type="buttonOutline"
              :icon="More"
              :loading="endButtonLoading"
              plain
              circle
            ></el-button>
            <template #dropdown v-if="!endButtonLoading"><el-dropdown-menu>
              <el-dropdown-item
                command="endChat"
                v-if="postChatFormEnabled && !endSessionButtonEnabled"
                >End Chat</el-dropdown-item
              >
              <el-dropdown-item
                v-if="!sessionEnded && sessionButtonReady && !settings.liveChatStatus"
                command="endSession"
                >End Chat Session</el-dropdown-item
              >
              <el-dropdown-item command="clearCache">
                {{ clearButtonText }}
              </el-dropdown-item>
              <el-dropdown-item v-if="showHistoryDownload" command="downloadHistory">
                Download History
              </el-dropdown-item>
            </el-dropdown-menu></template>
          </el-dropdown>
          <el-button
            v-else
            class="floating-mobile-button mr-3"
            @click="openHeaderMenu"
            :type="buttonOutline"
            :loading="endButtonLoading"
            circle
            plain
          >
            <svg
              class="floating-mobile-menu-icon"
              width="24"
              height="100%"
              viewBox="0 0 25 25"
              xmlns="http://www.w3.org/2000/svg"
              :fill="headerModalIconColorMobile"
            >
              <path
                d="M4 4h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 10h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 16h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4z"
              />
            </svg>
          </el-button>
        </div>
        <div v-show="!(lastVideo || selectedVideo) && !isMinimizeVideo">
          <TrendingTopics
            @sendPostback="sendPostback"
            :isOpenList="isOpenTrendingTopic"
            @handleTrendingList="handleTrendingTopicList"
          />
        </div>
      </el-header>
      <transition-group
        name="el-zoom-in-top"
        v-if="(lastVideo || selectedVideo) && !isMinimizeVideo"
      >
        <el-main id="keyreply-panel-body" class="keyreply-video" key="main">
          <Video
            :videoBotMessage="selectedVideo ? selectedVideo : lastVideo"
            :agents="agents"
            :chatbox="chatbox"
            @sendPostback="sendPostback"
            @setLightBoxImage="lightBoxImage = $event"
            @sendMessage="sendMessage"
            @handleTypingMessage="handleTypingMessage"
            @handleMinimizeVideo="handleMinimizeVideo"
            @resetSelectedVideo="resetSelectedVideo"
          />
        </el-main>
      </transition-group>
      <transition-group name="el-zoom-in-top" v-else>
        <el-main
          v-show="webviewUrl && showWebview"
          id="keyreply-panel-frame"
          key="iframe"
          class="overflow-hidden panel-with-top"
          :style="mainPositionStyle"
        >
          <el-button
            circle
            v-if="returnToChatButton"
            :icon="Back"
            type="info"
            class="absolute bottom-[8%] left-[5%]"
            @click="closeWebView"
          ></el-button>
          <!-- <WebviewDownloader :webview-url="iframeWebviewUrl" /> -->
          <iframe
            :src="iframeWebviewUrl"
            ref="iframe"
            class="h-full w-full"
            frameborder="0"
            allowfullscreen="true"
            allowtransparency
          >
          </iframe>
        </el-main>
        <el-main v-if="toShowPreChatForm" key="main" id="keyreply-panel-body-form-pre" ref="window">
          <PreChatForm
            :settings="settings"
            @setShowPreChatForm="setShowPreChatForm"
            @onPreChatFormSuccess="onPreChatFormSuccess"
          ></PreChatForm>
        </el-main>
        <el-main
          v-else-if="toShowPostChatForm"
          key="main1"
          id="keyreply-panel-body-form-post"
          ref="window"
        >
          <PostChatForm
            :settings="settings"
            @setShowPostChatForm="setShowPostChatForm"
            @clearCache="clearCache"
          ></PostChatForm>
        </el-main>
        <el-main
          v-else
          v-show="!webviewUrl || !showWebview"
          id="keyreply-panel-body"
          key="main2"
          ref="window"
          :style="panelBodyCssVars"
          class="panel-with-top panel-with-background"
          @scroll="onWidgetScroll"
        >
          <div
            v-if="settings.subtext.text"
            class="keyreply-subtle-link"
            :class="{ 'mt-[35px]': trendingTopics.length > 0 }"
          >
            <a
              :href="settings.subtext.url"
              class="text-[rgba(0,0,0,0.4)] no-underline"
              target="_blank"
              >{{ settings.subtext.text }}</a
            >
          </div>
          <div v-else-if="showMoreMessagesButton" class="flex justify-center">
            <el-button
              link
              :loading="showingMoreMessages"
              class="keyreply-subtle-link mb-[10px] p-0"
              :class="{ 'mt-[35px]': trendingTopics.length > 0 }"
              @click="fetchHistory"
            >
              - show more message -
            </el-button>
          </div>

          <div
            class="keyreply-subtle-link mb-[10px]"
            :class="{ 'mt-[35px]': trendingTopics.length > 0 }"
            v-else-if="showLoadAllMessagesButton"
          >
            - loaded all messages -
          </div>
          <div
            :class="!isMobileMode ? 'keyreply-powered' : 'keyreply-powered mobile-mode'"
            v-else-if="!showLoadAllMessagesButton && isUiBetaEnabled"
          >
            <span>Powered By <span style="color: #a6b5e6">RisingPoint</span> </span>
          </div>
          <br />

          <message
            v-for="(message, index) in sortedMessages"
            :showQuickReply="showQuickReply(message, index)"
            :key="`message-${index}`"
            :message="message"
            :agents="agents"
            @sendPostback="sendPostback"
            @setLightBoxImage="lightBoxImage = $event"
            :maintainBannerHeight="maintainBannerHeight"
            :messageIndex="index"
            @handleExpandVideo="handleExpandVideo"
          />

          <el-row>
            <el-col class="mx-[25px]">
              <div
                :class="{
                  'opacity-100': settings.typingIndicator.status,
                  'opacity-0': !settings.typingIndicator.status,
                }"
              >
                <img
                  :src="typingIndicatorUrl"
                  class="w-[30px]"
                  v-if="!settings.liveChatStatus || !isUiBetaEnabled || !liveChatAgentName"
                />
                <div
                  v-else
                  class="text-[13px] mx-[-15px] my-[10px] flex items-center italic text-[#999999]"
                >
                  <span>{{ liveChatAgentName }} is typing</span>
                  <div class="typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </el-col>
          </el-row>
        </el-main>
      </transition-group>
      <el-footer
        v-if="showTextInput && showTextInputVideo"
        id="keyreply-panel-footer"
        height="auto"
      >
        <div
          v-if="
            suggestions.length > 0 &&
            showSuggestion &&
            chatboxHasValidLength &&
            !settings.liveChatStatus
          "
          class="keyreply-autocomplete-popper"
          @mouseleave="setSuggestIndex(-1)"
        >
          <div
            v-for="(suggestion, index) in suggestions"
            :key="index"
            :class="{
              'keyreply-suggestion-selected': suggestSelect === index,
              'pointer-events-none': lastEvent === 'keydown' && !$device.Mobile,
            }"
            class="keyreply-suggestion"
            @click="send"
            @mouseover="setSuggestIndex(index)"
          >
            {{ suggestion.item.value }}
          </div>
        </div>

        <el-dropdown
          v-if="showSideOptions"
          placement="top-start"
          :trigger="triggerOption"
          @command="footerButtonClick"
          class="flex-[0_0_36px]"
        >
          <div class="keyreply-footer-button" v-if="!isUiBetaEnabled">
            <el-icon><CirclePlus /></el-icon>
          </div>

          <div class="keyreply-footer-button keyreply-footer-button-icon" v-else>
            <el-icon><ArrowUp /></el-icon>
          </div>

          <template #dropdown><el-dropdown-menu>
            <el-dropdown-item
              command="upload photo"
              :icon="Picture"
              v-if="isFileUploadEnable"
              >Upload photo</el-dropdown-item
            >
            <el-dropdown-item
              command="upload document"
              :icon="Document"
              v-if="isFileUploadEnable"
              >Upload document</el-dropdown-item
            >
            <el-dropdown-item v-if="settings.ocrButton" :icon="Picture" command="ocr"
              >OCR</el-dropdown-item
            >
            <el-dropdown-item
              v-if="settings.shareLocation"
              :icon="Location"
              command="share location"
              >Share location</el-dropdown-item
            >
            <el-dropdown-item
              v-if="settings.enableVoice"
              :icon="Microphone"
              command="speech to text"
              >Toggle Voice</el-dropdown-item
            >
          </el-dropdown-menu></template>
        </el-dropdown>
        <transition name="el-fade-in">
          <div
            v-if="useAudioInput"
            class="keyreply-footer-button keyreply-mic-back-button"
            @click="micBackAction"
          >
            <el-icon><component :is="!audioFile ? ArrowRight : Close" /></el-icon>
          </div>
        </transition>
        <transition name="el-zoom-in-center">
          <AudioRecorder
            ref="audioRecorder"
            :audioFile="audioFile"
            @updateAudioFile="updateAudioFile"
            v-if="useAudioInput"
          />
          <div class="w-full p-[7px]" v-else-if="showPushToTalk">
            <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
            <el-button
              :type="listening ? 'danger' : 'success'"
              @mousedown="startListen"
              @touchstart="startListen"
              @mouseup="stopListen"
              @touchend="startListen"
              class="w-full"
              round
            >
              <span v-if="!listening">Push button to speak</span>
              <span v-else>
                <el-icon class="is-loading"><Loading /></el-icon>
                {{ chatbox || "Release to send" }}
              </span>
            </el-button>
          </div>

          <el-input
            v-else
            :disabled="
              disableTextInput ||
              toShowPreChatForm ||
              toShowPostChatForm ||
              sessionEnded ||
              botIsGenerateAnswer ||
              isLastMessageEndingChat
            "
            :autofocus="true"
            :placeholder="disableTextInput ? chooseOptionLabel : typingsLabel"
            v-model="chatbox"
            ref="textbox"
            type="textarea"
            spellcheck="true"
            :class="['keyreply-message-input', chatboxBorderLarge ? 'chatbox-radius-large' : 'chatbox-radius-small']"
            @keydown.enter.exact="send"
            @focus="focusInput()"
            @blur="toggleSuggestion(false, 300)"
            @keypress="setSuggestIndex(-1)"
            @input="resizeTextArea"
            :maxlength="settings.charLimit?.limit"
          />
        </transition>
        <transition name="el-fade-in">
          <div
            v-if="!useAudioInput && chatbox.length === 0 && settings.audioInputEnabled"
            class="keyreply-footer-button keyreply-mic-button"
            @click="openRecording"
          >
            <el-icon><Microphone /></el-icon>
          </div>
        </transition>

        <!-- input -->
        <div
          class="keyreply-footer-button keyreply-send-button"
          :class="{
            visible:
              !settings.audioInputEnabled ||
              (showSendButton && !showPushToTalk && !disableTextInput && !isLastMessageEndingChat),
            disabled:
              !isInternetConnectionAvailable ||
              !(filteredXSSMessage || audioFile) ||
              disableTextInput ||
              sendingAudio ||
              isLastMessageEndingChat,
            'pb-0 pt-[2px]': settings.charLimit?.showCountDown,
          }"
          @click="send"
        >
          <el-icon
            :class="{
              'is-loading': sendingAudio,
              '-mb-[10px]': settings.charLimit?.showCountDown,
            }"
          ><component :is="sendingAudio ? Loading : Promotion" /></el-icon>
          <span v-show="settings.charLimit?.showCountDown" class="text-[10px] text-[#989898]">
            {{ countdownLimitLabel }}
          </span>
        </div>

        <FileUploader ref="uploader" />
      </el-footer>
      <transition name="bounce">
        <div id="keyreply-scroll-button" v-if="isShowScrollButton && !isUserTyping">
          <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
          <el-button
            class="circle-button"
            :type="buttonOutline"
            :icon="Top"
            plain
            circle
          ></el-button>
          <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
          <el-button
            :type="buttonOutline"
            class="info-button"
            @click="scrollToStickyMenu"
          >
            <el-icon class="mb-[3px]"><ArrowUp /></el-icon>
            <p>{{ stickyMenuText }}</p>
          </el-button>
        </div>
      </transition>
      <div class="btn-video-expand" @click="handleMinimizeVideo(false)" v-if="isMinimizeVideo">
        <el-icon><VideoPlay /></el-icon>
      </div>
    </el-container>
    <web-cam-dialog
      :dialog-visible="showWebCamDialog"
      :data="webCamData"
      @close="showWebCamDialog = false"
    />
    <el-dialog
      class="keyreply-email-dialog"
      v-model="emailFormVisible"
      :modal="false"
    >
      <template #header>Forward chat history</template>
      <el-form ref="emailForm" :model="form" :rules="emailForm">
        <el-form-item label="Email" prop="userEmail">
          <el-input
            ref="userEmail"
            v-model="form.userEmail"
            autocomplete="off"
            clearable
            @keydown.enter="emailFormSubmit"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="emailFormVisible = false">Cancel</el-button>
          <el-button type="primary" @click="emailFormSubmit">Confirm</el-button>
        </span>
      </template>
    </el-dialog>

    <div v-if="lightBoxImage" class="lightbox">
      <div class="full-lightbox" @click="lightBoxImage = null" />
      <a class="lightbox-close-button" @click.prevent="lightBoxImage = null">&times;</a>
      <img class="image-lightbox" :src="lightBoxImage" />
    </div>

    <audio
      id="keyreply-notification-sound"
      hidden
      muted
      :src="notificationSoundUrl"
      type="audio/ogg"
    />
  </div>
</template>

<script lang="ts">
import { h, defineComponent } from "vue";

/**
 * Local v-style component for dynamic style injection
 * Vue 3 compatible - uses render function with h()
 * Defined in separate <script> block so it can be referenced in defineOptions()
 */
export const VStyle = defineComponent({
  name: "v-style",
  render() {
    return h("style", this.$slots.default ? this.$slots.default() : []);
  },
});
</script>

<script setup lang="ts">
/**
 * @fileoverview ChatWindow main component
 * @vue3migration - Phase 3H: Full <script setup> conversion
 *
 * Converted from Options API to Composition API with <script setup>
 */
import { useMainStore } from "@/stores/main";
import Message from "./Message/Index.vue";
import Video from "./Video/Index.vue";
import { v4 as uuid } from "uuid";
import * as _ from "lodash-es";
import advancedFormat from "dayjs/plugin/advancedFormat";
import dayjs from "dayjs";
import saveAs from "file-saver";
import WebCamDialog from "@/components/WebCamDialog.vue";
import PreChatForm from "@/components/PreChatForm.vue";
import PostChatForm from "@/components/PostChatForm.vue";
import {
  ref,
  reactive,
  shallowRef,
  computed,
  watch,
  onMounted,
  onBeforeMount,
  onBeforeUnmount,
  nextTick,
  useTemplateRef,
  getCurrentInstance,
} from "vue";
import FileUploader from "./FileUploader/Index.vue";
import Push from "push.js";
import { filterXSS } from "xss";
import getParamsFromUrl from "@/helpers/getParamsFromUrl";
import { parseLanguageBasedLabel } from "@/helpers/langLabels";
import TrendingTopics from "./TrendingTopics/Index.vue";
import signalRHubConnection from "@/helpers/signalRHubConnection";
import AudioRecorder from "./AudioRecorder.vue";
import { marked } from "marked";
import { ElNotification } from "element-plus";
import {
  FullScreen,
  Delete,
  Download,
  More,
  ArrowDown,
  ArrowUp,
  ArrowRight,
  WarningFilled,
  Service,
  Close,
  CirclePlus,
  Picture,
  Document,
  Location,
  Microphone,
  Loading,
  Promotion,
  Top,
  VideoPlay,
  Back,
  BottomLeft,
} from "@element-plus/icons-vue";
import { markHTML } from "@/filters";
import { useChatMessages } from "@/composables/useChatMessages";
import { useChatSession, type SessionStore } from "@/composables/useChatSession";
import { useChatWindowUI } from "@/composables/useChatWindowUI";
import { useVideoPlayer } from "@/composables/useVideoPlayer";
import { useSpeechToText } from "@/composables/useSpeechToText";
import { useAudioRecording } from "@/composables/useAudioRecording";
import { useWebview, type WebviewStore } from "@/composables/useWebview";
import { useChatHistory } from "@/composables/useChatHistory";
import { useSignalRConnection } from "@/composables/useSignalRConnection";
import { applyWidgetSizeStyle } from "./widgetSize";

dayjs.extend(advancedFormat);

// ====================
// Component Options
// ====================
defineOptions({
  name: "ChatWindow",
  components: {
    Message,
    Video,
    WebCamDialog,
    PreChatForm,
    FileUploader,
    PostChatForm,
    TrendingTopics,
    AudioRecorder,
    VStyle,
  },
});

// ====================
// Props & Emits
// ====================
const props = defineProps<{
  isWidgetReadyToDrag?: boolean;
  widgetPos?: { left: number | null; top: number | null };
}>();

const emit = defineEmits<{
  (e: "handleGrabElement", payload: { event: MouseEvent; elementId: string }): void;
}>();

// ====================
// Global Properties
// ====================
const instance = getCurrentInstance();
const globalProps = instance?.appContext.config.globalProperties;
const $device = globalProps?.$device;
const $listen = globalProps?.$listen;
const $speak = globalProps?.$speak;

// ====================
// Store
// ====================
const store = useMainStore();

// ====================
// Template Refs
// ====================
const keyReplyChatWindow = useTemplateRef<any>("keyReplyChatWindow");
const tabs = useTemplateRef<any>("tabs");
const iframe = useTemplateRef<HTMLIFrameElement>("iframe");
const windowRef = useTemplateRef<any>("window");
const textbox = useTemplateRef<any>("textbox");
const uploader = useTemplateRef<any>("uploader");
const emailFormRef = useTemplateRef<any>("emailForm");
const userEmailRef = useTemplateRef<any>("userEmail");
const audioRecorderRef = useTemplateRef<any>("audioRecorder");

// ====================
// Composables
// ====================

// Chat messages composable
const chatMessages = useChatMessages({
  messages: computed(() => store.messages),
  settings: computed(() => store.settings),
  fuse: computed(() => store.fuse),
});

// Chat session composable
const chatSession = useChatSession({
  store: store as unknown as SessionStore,
});

// Chat window UI composable
const chatWindowUI = useChatWindowUI({
  widgetPos: props.widgetPos || { left: null, top: null },
  isWidgetReadyToDrag: props.isWidgetReadyToDrag || false,
  settings: computed(() => store.settings),
  isUiBetaEnabled: computed(() => store.isUiBetaEnabled),
  isMobile: $device?.Mobile ?? false,
  isEnableNotification: computed(() => store.isEnabledNotification && Push.Permission.has()),
  liveChatStatus: computed(() => store.settings?.liveChatStatus ?? false),
  isConnected: computed(() => store.isInitiating || store.socketConnectionStatus),
  tallViewSettings: (window as any).kr_settings?.tallView ?? false,
});

// Video player composable
const videoPlayer = useVideoPlayer({
  sortedMessages: chatMessages.sortedMessages,
});

// Speech to text composable
const speechToText = useSpeechToText({
  $listen,
  $speak,
  chatbox: chatMessages.chatbox,
  showSuggestion: chatMessages.showSuggestion,
  send: () => send(),
});

// Audio recording composable
const audioRecording = useAudioRecording({
  chatbox: chatMessages.chatbox,
  audioRecorderRef: audioRecorderRef as any,
});

// Webview composable
const webview = useWebview({
  store: store as unknown as WebviewStore,
  settings: computed(() => ({ tabs: store.settings?.tabs })),
});

// Chat history composable
const chatHistory = useChatHistory({
  store: store,
  userSourceExist: chatWindowUI.userSourceExist,
  isMobileMode: chatWindowUI.isMobileMode,
  iOSSDK: $device?.iOSSDK ?? false,
});

// SignalR connection composable
const signalR = useSignalRConnection({
  store: store,
  messages: computed(() => store.messages),
  semanticServiceUrl: store.settings?.semanticServiceUrl,
});

// ====================
// Local State (from data())
// ====================
const showWebCamDialog = ref(false);
const showingMoreMessages = ref(false);
const livechat_ongoing = ref(true);
const lastEvent = ref<string | null>(null);
const uploading = ref(false);
const lightBoxImage = ref<string | null>(null);
const toShowPreChatForm = ref(false);
const toShowPostChatForm = ref(false);
const isInternetConnectionAvailable = ref(navigator.onLine);
const isOpenTrendingTopic = ref(false);
const lastUserMessage = ref("");
const loading = ref(false);
const isUploadable = ref(false);

// Device-specific (constants from $device)
const iOSSDK = $device?.iOSSDK ?? false;
const triggerOption = $device?.iOS || $device?.iOSSDK ? "click" : "hover";

// WebCam data
const webCamData = reactive({
  senderId: store.uid,
  recipientId: location.hostname,
});

// Email form data
const form = reactive({
  userEmail: "",
});

// Email validation
const validateEmail = (rule: any, value: string, callback: (error?: Error) => void) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const validEmail = re.test(String(value).toLowerCase());
  const emailDomainWhitelist = store.emailDomainWhitelist || [];

  if (value === "") {
    callback(new Error("Please input the email address."));
    return;
  } else if (!validEmail) {
    callback(new Error("Please input valid email address."));
    return;
  } else if (!_.isEmpty(emailDomainWhitelist)) {
    const userDomainIsIncludedInWhitelist = _.find(emailDomainWhitelist, (domain: string) =>
      value.includes(domain)
    );

    if (!userDomainIsIncludedInWhitelist) {
      callback(new Error("Receiver email is not in whitelist. Please contact admin."));
      return;
    }
  }

  callback();
};

const emailForm = reactive({
  userEmail: [{ validator: validateEmail, trigger: "change" }],
});

// Expose composable refs for template
const {
  chatbox,
  suggestions,
  suggestSelect,
  showSuggestion,
  hideSuggestion,
  sortedMessages,
  firstMessage,
  lastMessage,
  lastBotMessage,
  filteredXSSMessage,
  chatboxHasValidLength,
  messageSize,
  debounceSuggestionListing,
  changeSuggestion,
  toggleSuggestion,
  setSuggestIndex,
} = chatMessages;

const {
  sessionEnded,
  sessionButtonReady,
  endSessionButtonEnabled,
  endButtonLoading,
  postChatFormEnabled,
} = chatSession;

const {
  tallView: tall,
  isShowScrollButton,
  lastScrollTop,
  toShowHeaderMenu,
  chatInputHeight,
  maintainBannerHeight,
  isShowMaintenanceBanner,
  isMobileMode,
  persistHeader,
  isMobileModeDefault,
  userSourceExist,
  contentOffset,
  showingNotifBanner,
  isShowWidgetSizeSection,
  headerClass,
  bannerClass,
  mainPositionStyle,
  floatingButtonClass,
  chatWindowRight,
  chatWindowBottom,
  chatWindowHeight,
  chatWindowTop,
  chatWindowLeft,
  chatboxBorderLarge,
  chatWindowCssVars,
  openHeaderMenu,
  closeHeaderMenu,
  setMaintainBannerHeight,
  closeMaintenanceBanner,
  updateScrollButtonVisibility,
  showScrollButton,
  hideScrollButton,
} = chatWindowUI;

const {
  selectedVideo,
  isMinimizeVideo,
  lastVideo,
  showTextInputVideo,
  handleMinimizeVideo: videoHandleMinimizeVideo,
  handleExpandVideo: videoHandleExpandVideo,
  resetSelectedVideo: videoResetSelectedVideo,
} = videoPlayer;

const {
  voice,
  listening,
  showPushToTalk,
} = speechToText;

const {
  useAudioInput,
  audioFile,
  sendingAudio,
} = audioRecording;

const {
  webviewUrl,
  showWebview,
  returnToChatButton,
  iframeWebviewUrl,
} = webview;

const {
  emailFormVisible,
} = chatHistory;

const {
  botIsGenerateAnswer,
  signalRConnectionId,
  tempGenerateMessage,
  tempGenerateMessageId,
} = signalR;

// ====================
// Computed Properties
// ====================

// Store accessors
const messages = computed(() => store.messages);
const open = computed(() => store.chatWindow);
const settings = computed(() => store.settings);
const isTextInputDisabled = computed(() => store.disableTextInput);
const stickyMenu = computed(() => store.stickyMenu);
const stickyMenuText = computed(() => store.stickyMenuText);
const isUiBetaEnabled = computed(() => store.isUiBetaEnabled);
const isRtlModeEnabled = computed(() => store.isRtlModeEnabled);
const stickyMenuLanguage = computed(() => store.stickyMenuLanguage);
const liveChatAgentName = computed(() => store.liveChatAgentName);
const trendingTopics = computed(() => store.trendingTopics);

const isConnected = computed(() => store.isInitiating || store.socketConnectionStatus);

const clearButtonText = computed(() =>
  endSessionButtonEnabled.value ? "Clear Cache" : "Clear History"
);

const isEnableNotification = computed(() => store.isEnabledNotification && Push.Permission.has());

const tallViewSettings = computed(() => (window as any).kr_settings?.tallView);

const showMoreMessagesButton = computed(
  () => !store.isInitiating && !store.newUser && !store.allPastMessagesFetched
);

const showLoadAllMessagesButton = computed(() => !store.newUser && store.allPastMessagesFetched);

const buttonOutline = computed(() => {
  switch (settings.value?.buttonOutlineColor) {
    case "red":
      return "danger";
    case "blue":
      return "primary";
    case "green":
      return "success";
    default:
      return "primary";
  }
});

const isMaintenanceBannerEnabled = computed(() =>
  _.get(settings.value, "maintenanceBanner.enabled", false)
);

const showTextInput = computed(() => {
  if (showWebview.value) return false;
  return true;
});

const disableTextInput = computed(
  () =>
    _.isEmpty(store?.uid) ||
    _.get(lastBotMessage.value, "options.disableTextInput", false) ||
    store.disableTextInput
);

const showSideOptions = computed(() => {
  return (
    isFileUploadEnable.value ||
    settings.value?.shareLocation ||
    settings.value?.enableVoice ||
    settings.value?.ocrButton
  );
});

/**
 * CSS variables for panel body (combines position and background)
 * Used for CSP-compliant styling
 */
const panelBodyCssVars = computed(() => ({
  ...mainPositionStyle.value,
  '--panel-background': settings.value.background ? `url(${settings.value.background})` : 'none',
}));

/**
 * Converts a tab style object to CSS variables for CSP compliance
 * @param style - Style object or string from server settings
 * @returns Object with CSS variables
 */
function getTabStyleCssVars(style: Record<string, string> | string | undefined): Record<string, string> {
  if (!style) return {};

  // If it's already an object, convert keys to CSS variables
  if (typeof style === 'object') {
    const cssVars: Record<string, string> = {};
    for (const [key, value] of Object.entries(style)) {
      // Convert camelCase to kebab-case for CSS variable names
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      cssVars[`--tab-${cssKey}`] = value;
    }
    return cssVars;
  }

  // If it's a string, we cannot safely parse it for CSP - skip styling
  // Server should send object format for proper CSP support
  return {};
}

const isFileUploadEnable = computed(() => {
  const onlyAllowUploadWhenInLiveChat = settings.value?.onlyAllowUploadWhenInLiveChat;
  if (onlyAllowUploadWhenInLiveChat) {
    return settings.value?.fileUpload && settings.value?.liveChatStatus && isUploadable.value;
  }
  return settings.value?.fileUpload && isUploadable.value;
});

const showSendButton = computed(() => chatbox.value.length > 0 || audioFile.value);

const loadingText = computed(() =>
  loading.value ? "Forwarding chat history ..." : "Uploading files ..."
);

const agents = computed(() => {
  const agentNames = messages.value.map((message: any) => _.get(message, "data.meta.name", null));
  const uniqAgents = _.uniq(agentNames);
  return _.compact(uniqAgents);
});

const typingIndicatorUrl = computed(() => {
  const webchatUrl = _.get(window, "appConfig.WEBCHAT_URL");
  if (webchatUrl) {
    return new URL("typing_indicator.gif", webchatUrl as string).href;
  }
  return "https://keyreply.blob.core.windows.net/files/images/typing_indicator.gif";
});

const notificationSoundUrl = computed(() => {
  const webchatUrl = _.get(window, "appConfig.WEBCHAT_URL");
  if (webchatUrl) {
    return new URL("sound/notification.ogg", webchatUrl as string).href;
  }
  return "https://keyreply.blob.core.windows.net/files/sound/notification.ogg";
});

const showHistoryDownload = computed(() => _.get(settings.value, "showHistoryDownload", true));

const showClearCloneState = computed(() => {
  const params = getParamsFromUrl();
  return params.get("isSimulated");
});

const isUserTyping = computed(() => textbox.value?.focused ?? false);

const headerModalIconColor = computed(() => store.settings?.headerModalIconColor);

const headerModalIconColorMobile = computed(
  () => store.settings?.headerModalIconColorMobile || store.settings?.headerBackgroundColor
);

const widgetElement = computed(() => keyReplyChatWindow.value?.$el);

const isLastMessageEndingChat = computed(
  () =>
    lastMessage.value?.type === "postback" &&
    lastMessage.value?.data?.content?.[0]?.text === "End Live Chat"
);

const typingsLabel = computed(() =>
  parseLanguageBasedLabel(
    settings.value?.languageBasedLabels,
    "textareaTypingsPlaceholder",
    store.currentLanguage || settings.value?.defaultLanguage || "en",
    "Type your message"
  )
);

const chooseOptionLabel = computed(() =>
  parseLanguageBasedLabel(
    settings.value?.languageBasedLabels,
    "textareaChoicesPlaceholder",
    store.currentLanguage || settings.value?.defaultLanguage || "en",
    "Please choose an option"
  )
);

const countdownLimitLabel = computed(() => {
  const limit = settings.value?.charLimit?.limit || 1000;
  const current = chatbox.value?.length || 0;
  return `${current}/${limit}`;
});

// ====================
// Watchers
// ====================

watch(chatbox, (newText, oldText) => {
  if (newText) {
    showSuggestion.value = !hideSuggestion.value;
    suggestSelect.value = -1;
  }
  let newTextStr = typeof newText === "object" ? "" : newText;
  let oldTextStr = typeof oldText === "object" ? "" : oldText;
  if (settings.value?.liveChatStatus) {
    updateTypingIndicator(newTextStr);
  }
  debounceSuggestionListing(newTextStr);
});

watch(lastMessage, (newText, oldText) => {
  if (toShowPreChatForm.value) return;
  if (toShowPostChatForm.value) return;
  if (newText !== oldText) {
    nextTick(() => {
      if (settings.value?.newChatStyleScroll) {
        scrollUserMessageToTop();
      } else {
        scrollToBottom();
      }

      if (!$device?.Mobile) {
        textbox.value?.focus();
      }
    });
  }
});

// ====================
// Lifecycle Hooks
// ====================

onBeforeMount(() => {
  window.onmessage = (e: MessageEvent) => {
    if (e.data === "downloadcompleted") {
      closeWebView();
    }
  };
  window.addEventListener("message", crossOriginMessageHandler, false);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("touchmove", onTouchMove);
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("online", updateOnlineStatus);
  window.addEventListener("offline", updateOnlineStatus);
});

onMounted(async () => {
  if (Push.Permission.has()) {
    store.setNotificationPermission(true);
  }

  // Set up wheel event to prevent parent scroll when chat is at boundaries
  setupWheelEventHandler();

  if (settings.value?.tabs && !_.isEmpty(settings.value?.tabs)) {
    if (settings.value.tabs[0].url !== "main") {
      _.delay(() => {
        store.setWebviewUrl(settings.value?.tabs?.[0]?.url ?? "");
        store.setShowWebview(true);
        if (iframe.value) {
          iframe.value.style.cssText =
            settings.value?.tabsIframeStyle && settings.value?.tabsIframeStyle !== ""
              ? settings.value.tabsIframeStyle
              : "width:100%;height:100%;";
        }
      }, 100);
    }
  }

  if (settings.value?.fileUpload) {
    isFileUploadable();
  }
  resetPreChatFormVisibilityBySetting();

  // Set maintain banner height variable if enabled
  if (isMaintenanceBannerEnabled.value) {
    setMaintainBannerHeight();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("touchmove", onTouchMove);
  window.removeEventListener("online", updateOnlineStatus);
  window.removeEventListener("offline", updateOnlineStatus);
});

// ====================
// Methods
// ====================

function setupWheelEventHandler() {
  const el = instance?.proxy?.$el;
  if (el) {
    el.onwheel = (e: WheelEvent) => {
      const chatBody = windowRef.value?.$el;
      if (!chatBody) return;

      const atTop = chatBody.scrollTop <= 0;
      const atBottom = chatBody.scrollTop + chatBody.offsetHeight >= chatBody.scrollHeight;
      const scrollDirection = e.deltaY < 0 ? "up" : "down";

      if ((atTop && scrollDirection === "up") || (atBottom && scrollDirection === "down")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
  }
}

function onTouchMove() {
  lastEvent.value = "touchmove";
  textbox.value?.blur();
}

function onMouseMove() {
  lastEvent.value = "mousemove";
}

function onKeyDown(e: KeyboardEvent) {
  lastEvent.value = "keydown";
  if (showSuggestion.value === false || suggestions.value.length === 0) {
    return;
  }
  const code = e.keyCode ? e.keyCode : e.which;
  const upKey = code === 38;
  const downKey = code === 40;
  const escKey = code === 27;

  if (upKey) {
    changeSuggestion(-1);
    e.preventDefault();
  } else if (downKey) {
    changeSuggestion(1);
    e.preventDefault();
  } else if (escKey) {
    toggleSuggestion(false);
    suggestSelect.value = -1;
  }
}

function stripWhiteSpace(text: string) {
  return text.replace(/\s/g, "");
}

async function fetchHistory() {
  showingMoreMessages.value = true;
  await store.fetchHistory({ fetchMore: true });
  showingMoreMessages.value = false;
}

function focusInput() {
  toggleSuggestion(true);
  store.clearUnreadMessageCount();
}

function requestNotificationPermission() {
  store.requestNotificationPermission();
}

function setShowPreChatForm(toShow: boolean) {
  toShowPreChatForm.value = toShow === true;
}

function setShowPostChatForm(toShow: boolean) {
  toShowPostChatForm.value = toShow === true;
}

function showQuickReply(currentMessage: any, index: number) {
  const lastMsg = _.last(sortedMessages.value);
  const isLastMessage = currentMessage?.id === lastMsg?.id;
  const displayMode = _.get(currentMessage, "options.quickReplyDisplayMode", "default");

  return (
    isLastMessage ||
    displayMode === "always" ||
    (displayMode === "default" && settings.value?.alwaysShowQuickReply)
  );
}

const sendMessage = _.debounce(
  function (payload: any) {
    store.sendMessage(payload).catch((error: any) => {
      const errType = error?.response?.data?.error;
      const errorMessage = error?.response?.data?.message;
      console.log(error);
      const message =
        errType === "[RequestValidation]"
          ? "Your message could not be sent due to a validation error. Please check your input."
          : errorMessage || "Failed to send your message, please try again later.";
      ElNotification.error({
        title: "Error",
        message,
        duration: 2000,
        position: "bottom-right",
        offset: getNotifyBoxOffset(),
        customClass: getNotifyBoxCustomClass(),
      });
    });
  },
  200,
  { leading: true }
);

const updateTypingIndicator = _.debounce(
  function (text: string | boolean) {
    let status = false;
    if (text) {
      status = true;
    }
    store.updateAgentTypingIndicator(status);
  },
  500,
  { leading: true }
);

function resizeTextArea() {
  const textarea = textbox.value?.$refs?.textarea;
  if (!textarea) return;
  textarea.style.height = "38px";
  const textAreaHeight = Math.max(textarea.scrollHeight, 38);
  textarea.style.height = textAreaHeight - 20 + "px";
  chatInputHeight.value = textAreaHeight - 20;
}

function footerButtonClick(command: string) {
  switch (command) {
    case "upload document":
      uploader.value?.onUpload("document");
      break;
    case "upload photo":
      uploader.value?.onUpload("image");
      break;
    case "ocr":
      showWebCamDialog.value = true;
      break;
    case "share location":
      sendLocationMessage();
      break;
    case "speech to text":
      toggleVoice();
      break;
    default:
      return;
  }
}

function crossOriginMessageHandler(evt: MessageEvent) {
  const messageData = evt.data;
  const isOriginAcceptable = _.find(settings.value?.tabs, (o: any) => o.url.includes(evt.origin));
  if (typeof messageData === "string" && isOriginAcceptable) {
    if (tabs.value) {
      (tabs.value as any).currentName = "main";
    }
    store.setShowWebview(false);
    sendMessage({ text: messageData });
  }
  if (typeof messageData === "object") {
    try {
      const event = _.get(messageData, "krDispatcher.event");
      const payload = _.get(messageData, "krDispatcher.payload");
      const close = _.get(messageData, "krDispatcher.close");
      (window as any).$keyreply.dispatch(event, payload);
      if (close) {
        (window as any).$keyreply.dispatch("CLOSE_WEBVIEW");
      }
    } catch (error) {
      return;
    }
  }
}

function isFileUploadable() {
  if (!settings.value?.fileUpload) {
    console.error("file upload is not enabled");
    isUploadable.value = false;
    return;
  }
  isUploadable.value = true;
}

async function renewSession() {
  await store.start();
}

function emailFormSubmit(event?: Event) {
  if (event) {
    event.preventDefault();
  }
  emailFormRef.value?.validate((valid: boolean) => {
    if (valid) {
      let loadingClosed = false;
      loading.value = true;

      const offset = getNotifyBoxOffset();
      const customClass = getNotifyBoxCustomClass();

      setTimeout(() => {
        if (!loadingClosed) {
          loadingClosed = true;
          ElNotification({
            title: "Error",
            message: "Request to forward chat history time out.",
            duration: 2000,
            type: "error",
            position: "bottom-right",
            offset,
            customClass,
          });
          loading.value = false;
        }
      }, 15000);

      store
        .emailHistory(form)
        .then((result: any) => {
          loadingClosed = true;
          ElNotification.success({
            title: "Success",
            message: "Chat history sent!",
            duration: 2000,
            position: "bottom-right",
            offset,
            customClass,
          });
          loading.value = false;
          form.userEmail = "";
        })
        .catch((error: any) => {
          if (!loadingClosed) {
            loadingClosed = true;
            ElNotification.error({
              title: "Error",
              message: "Error forwarding chat history.",
              duration: 2000,
              position: "bottom-right",
              offset,
              customClass,
            });
            loading.value = false;
          }
        });
    } else {
      return false;
    }
  });
  emailFormVisible.value = false;
}

function tapHandler(e: Event) {
  e.stopImmediatePropagation();
  e.preventDefault();
  closeWindow();
}

function closeWindow() {
  if (iOSSDK) {
    const url = "keyreplysdk://?alert=close";
    window.location.href = url;
  }
  store.closeChatWindow();
}

function handleCommand(command: string) {
  switch (command) {
    case "clearCache":
      clearCache();
      break;
    case "downloadHistory":
      downloadHistory();
      break;
    case "clearCloneState":
      clearCloneState();
      break;
    case "endChat":
      endChat();
      break;
    case "endSession":
      endSession();
      break;
  }
}

function handleTabClick(clickedTab: { name: string }) {
  if (clickedTab.name === "main") {
    store.setShowWebview(false);
  } else {
    store.setShowWebview(true);
    if (clickedTab.name !== store.webviewUrl) {
      store.setWebviewUrl(clickedTab.name);
      const tab = _.find(settings.value?.tabs, (t) => t.url === clickedTab.name);
      if (tab && iframe.value) {
        const style = tab.style;
        if (style) {
          iframe.value.style.cssText = typeof style === 'string'
            ? style
            : Object.entries(style).map(([k, v]) => `${k}:${v}`).join(';');
        } else {
          iframe.value.style.cssText = "width:100%;height:100%";
        }
      }
    }
  }
}

function closeWebView() {
  store.closeWebview();
  store.setShowWebview(false);
  store.setReturnToChat(false);
  nextTick(scrollToBottom);
}

function allowDrop(ev: DragEvent) {
  ev.preventDefault();
}

function resetPreChatFormVisibilityBySetting() {
  const toShow = _.get(settings.value, "showPreChatForm.enabled", false);
  setShowPreChatForm(toShow);
}

async function onPreChatFormSuccess(payload: any) {
  await _start({ with_register: false, with_login: false });
}

function resetPostChatFormVisibilityBySetting() {
  setShowPostChatForm(postChatFormEnabled.value);
}

async function _stop({ with_clear_cache = false }) {
  await store.stopLivechat();
  await store.logout();
  if (with_clear_cache) {
    await store.clearCache();
  }
}

async function _start({ with_register = false, with_login = false }) {
  if (with_register) {
    await store.register();
  }
  if (with_login) {
    await store.login();
  }
  await store.listen();
  store.setGreeted(true);
  await store.sendStartMessage();
}

async function clearCache() {
  const confirm = window.confirm(
    settings.value?.clearAuthorizedUserDataOnClearButton
      ? "This will clear all user data on this browser and you will start chat as new user"
      : `Clear ${endSessionButtonEnabled.value ? "cache" : "history"}?`
  );
  if (confirm) {
    closeHeaderMenu();

    if (settings.value?.clearAuthorizedUserDataOnClearButton) {
      await store.clearAuthorizedUserData();
    } else {
      const defaultLanguage = store.settings?.defaultLanguage || "ms";
      localStorage.setItem("currentLanguage", defaultLanguage);
      store.setCurrentLanguage(defaultLanguage);
      isOpenTrendingTopic.value = false;
      await _stop({ with_clear_cache: true });
      await _start({ with_register: true, with_login: true });
      resetPreChatFormVisibilityBySetting();
      resetSelectedVideo();
      sessionEnded.value = false;
    }
  }
}

async function clearCloneState() {
  await store.clearCloneState();
}

async function downloadHistory() {
  let idtext = `\r\nuid: ${store.uid}`;
  const eceActivityId = store.settings?.eceActivityId;
  if (eceActivityId) {
    idtext = `\r\nactivityId: ${eceActivityId}`;
  }
  let text = `---- User Info ----
      ${idtext}
			\r\ntimestamp_generated: ${dayjs().format("DD-MM-YYYY HH:mm:ss")}
			${!userSourceExist.value ? `\r\nurl: ${window.location.href.split("?")[0]}` : ""}
			\r\n
			\r\n---- Chat history ----
      \r\n`;
  const chat = _.sortBy(store.messages, (message: any) => message.timestamp);
  _.each(chat, (message: any) => {
    text += dayjs(message.timestamp).format("DD-MM-YYYY HH:mm:ss");
    let prompt = " bot: ";
    if (message.type === "message" || message.type === "postback") {
      prompt = " user: ";
    } else if (message.type === "agent") {
      prompt = " agent: ";
    }
    text += prompt;
    text += _.map(message.data.content, (content: any) => {
      if (!_.isEmpty(content.text)) {
        return content.text;
      } else if (!_.isEmpty(content.files)) {
        return "[File Content]";
      } else if (!_.isEmpty(content.images)) {
        return "[Image Content]";
      } else {
        return "user connected";
      }
    }).join(" | ");
    text += "\r\n";
    if (store.settings?.historyHideAgentName) {
      text = text.replace(/bot: (.+) has joined the chat\./, "bot: Agent has joined the chat.");
    }
  });

  const useEmailDelivery = _.get(settings.value, "historyDeliveryMode") == "EMAIL";
  if (useEmailDelivery || iOSSDK || isMobileMode.value) {
    emailFormVisible.value = true;
    nextTick(() => {
      userEmailRef.value?.focus();
    });
  } else if (settings.value?.historyDeliveryMode === "PDF") {
    const [{ PDFDocument, StandardFonts, rgb }, fontkitModule] = await Promise.all([
      import("pdf-lib"),
      import("@pdf-lib/fontkit"),
    ]);
    const fontkit = (fontkitModule as any).default ?? fontkitModule;
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);

    let fontUrl = "https://keyreply.blob.core.windows.net/files/fonts/NotoSans-Regular.ttf";
    const webchatUrl = _.get(window, "appConfig.WEBCHAT_URL");
    if (webchatUrl) {
      fontUrl = new URL("fonts/NotoSans-Regular.ttf", webchatUrl as string).href;
    }
    const fontBytes = await fetch(fontUrl).then((res) => res.arrayBuffer());
    const font = await pdfDoc.embedFont(fontBytes);

    const x = 32;
    const lineHeight = 8;
    const maxCharsPerLine = 125;

    let page = pdfDoc.addPage();
    let { width, height } = page.getSize();
    let y = height - 32;

    const lines = text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      let words = lines[i].split(" ");
      let line = "";

      while (words.length > 0) {
        if (y < lineHeight * 2) {
          page = pdfDoc.addPage();
          ({ width, height } = page.getSize());
          y = height - 32;
        }
        while (words.length > 0 && (line + words[0]).length < maxCharsPerLine) {
          line += words.shift() + " ";
        }
        page.drawText(line.trim(), { x, y, size: lineHeight, font, color: rgb(0, 0.53, 0.71) });
        y -= lineHeight;
        if (line.trim() !== "") {
          y -= lineHeight;
        }
        line = "";
      }
    }

    const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true });
    saveAs(pdfDataUri, "chat_history.pdf");
  } else {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "chat_history.txt");
  }
}

function endChat() {
  setShowPreChatForm(false);
  resetPostChatFormVisibilityBySetting();
}

function scrollToBottom() {
  const box = windowRef.value;
  if (!box?.$el) return;

  const userMessages = box.$el.querySelectorAll(".user-message");
  if (!userMessages.length) {
    box.$el.scrollTop = box.$el.scrollHeight;
    return;
  }

  const lastUserMsg = userMessages[userMessages.length - 1];
  lastUserMsg.scrollIntoView({ block: "end" });
  box.$el.scrollTop += 300;
}

function scrollUserMessageToTop() {
  const box = windowRef.value;
  if (!box?.$el) return;

  const userMessages = box.$el.querySelectorAll(".user-message");
  if (!userMessages.length) return;

  const lastUserMsg = userMessages[userMessages.length - 1];
  const messageTop = lastUserMsg.offsetTop;
  box.$el.scrollTop = messageTop - 50;
}

function sendLocationMessage() {
  try {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const crd = pos.coords;
        sendMessage({
          location: { long: crd.longitude, lat: crd.latitude },
        });
      },
      (error) => {},
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 1000,
      }
    );
  } catch (error) {
    // No geolocation
  }
}

function sendPostback(button: any) {
  if (!isInternetConnectionAvailable.value) {
    ElNotification.error({
      title: "Error",
      message: "Your internet connection is not working, please try again later.",
      duration: 2000,
      position: "bottom-right",
      offset: getNotifyBoxOffset(),
      customClass: getNotifyBoxCustomClass(),
    });
    return;
  }

  let {
    text,
    event,
    data,
    url,
    phone,
    isLocation,
    popup,
    type,
    options = {},
    trendingText = "",
    intentName = "",
    trackUrl,
    $event,
  } = button;

  if (event === "set") {
    const language = data?.data?.entities_last?.languageDetect;
    if (language) {
      store.setCurrentLanguage(language);
      localStorage.setItem("currentLanguage", language);
    }
  }
  button.active = true;

  if (typeof data === "string" || typeof url === "string") {
    store.clickIntent({ text, event, type, data, url, intentName });
  }

  if (event === "$location" || isLocation) {
    sendLocationMessage();
  }

  if (event === "$tab") {
    store.setShowWebview(true);
    if (store.webviewUrl === data) {
      if (tabs.value) {
        (tabs.value as any).currentName = data;
      }
      if (iframe.value) {
        iframe.value.contentWindow?.postMessage(lastUserMessage.value, data);
      }
      return;
    }

    store.setWebviewUrl(data);
    const tab = _.find(settings.value?.tabs, (t) => t.url === data);
    if (tab && iframe.value) {
      const style = tab.style;
      if (style) {
        iframe.value.style.cssText = typeof style === 'string'
          ? style
          : Object.entries(style).map(([k, v]) => `${k}:${v}`).join(';');
      } else {
        iframe.value.style.cssText = "width:100%;height:100%";
      }
      if (tabs.value) {
        (tabs.value as any).currentName = data;
      }

      iframe.value.onload = () => {
        setTimeout(() => {
          iframe.value?.contentWindow?.postMessage(lastUserMessage.value, data);
        }, 2000);
      };
    }
  }

  if (event === "$display") {
    if ($event && typeof $event.preventDefault === "function") {
      $event.preventDefault();
    }

    if (data.type === "iframe" || data.type === "popup") {
      const parent = instance?.proxy?.$parent as any;
      if (parent) {
        parent.lightboxContent = data.url;
      }
      store.openDialog();
      return;
    }

    if (data.type === "tab") {
      const win = window.open(data.url, "_blank");
      if (win) {
        win.focus();
      }
    }

    if (data.type === "inline") {
      const dataURL = url || data?.url;
      if (_.includes(dataURL, "download=1")) {
        window.open(
          dataURL,
          "targetWindow",
          `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=400,height=600`
        );
      } else {
        const hideBackButton = _.get(data, "hideBackButton", false);
        store.setWebviewUrl(data.url);
        store.setShowWebview(true);
        store.setReturnToChat(!hideBackButton);
      }
      return;
    }
  }

  if (event === "$zopim") {
    if ((window as any).$zopim) {
      const action = _.get((window as any).$zopim, data);
      if (action && typeof action === "function") {
        action();
      }
    }
  }

  if (event !== "$navigate") {
    if (url) {
      if (trackUrl) {
        store.sendPostback({
          event: "track_url",
          data: url,
          text: "",
          options: {},
        });
      }
    } else if (phone) {
      const phone_regrex =
        /((?:\+|00)[17](?: |\-)?|(?:\+|00)[1-9]\d{0,2}(?: |\-)?|(?:\+|00)1\-\d{3}(?: |\-)?)?(0\d|\([0-9]{3}\)|[1-9]{0,3})(?:((?: |\-)[0-9]{2}){4}|((?:[0-9]{2}){4})|((?: |\-)[0-9]{3}(?: |\-)[0-9]{4})|([0-9]{6}))$/;
      const valid_number = phone_regrex.exec(phone);
      if (valid_number) {
        const action = type === "phone" ? "tel:" : "sms:";
        window.open(action + phone);
        store.sendPostback({
          event: type === "phone" ? "track_phone" : "track_sms",
          data: phone,
          text: "",
          options: {},
        });
      }
    } else {
      store.sendPostback({ event, data, text, options, trendingText });
    }

    if (data && data.clientEvent && data.clientData) {
      if (data.clientEvent === "SHOW_TEXTBAR") {
        store.showTextbar(data.eventData);
      }
    }
  }
}

function send(event?: Event) {
  const charLimit = settings.value?.charLimit?.limit || 1000;
  if (chatbox.value?.length > charLimit) {
    ElNotification.error({
      title: "Error",
      message: `Your message is too long. Maximum ${charLimit} characters allowed.`,
      duration: 3000,
      position: "bottom-right",
      offset: getNotifyBoxOffset(),
      customClass: getNotifyBoxCustomClass(),
    });
    return;
  }

  if (containsMarkdown(chatbox.value)) {
    ElNotification.error({
      title: "Error",
      message: "Markdown is not supported.",
      duration: 3000,
      position: "bottom-right",
      offset: getNotifyBoxOffset(),
      customClass: getNotifyBoxCustomClass(),
    });
    return;
  }

  if (event) {
    event.preventDefault();
    // Check for shift+enter to add newline (only for keyboard events)
    if (event instanceof KeyboardEvent && event.shiftKey) {
      chatbox.value += "\n";
      return;
    }
    if (event.target) {
      (event.target as HTMLTextAreaElement).rows = 1;
    }
  }

  if (!isInternetConnectionAvailable.value) {
    ElNotification.error({
      title: "Error",
      message: "Your internet connection is not working, please try again later.",
      duration: 2000,
      position: "bottom-right",
      offset: getNotifyBoxOffset(),
      customClass: getNotifyBoxCustomClass(),
    });
    return;
  }

  // handling for Audio Message
  if (audioFile.value) {
    const currentDate = new Date();
    const filename =
      store.uid + "_" + currentDate.toISOString().slice(0, 19).replace(/[-T:]/g, "");
    const file = new File([audioFile.value], `${filename}.wav`, { type: audioFile.value.type });
    const files = [file];
    confirmFileUpload(files);
    return;
  }

  if (
    showSuggestion.value &&
    suggestions.value &&
    suggestions.value.length > 0 &&
    suggestSelect.value > -1
  ) {
    const suggestion = suggestions.value[suggestSelect.value];
    if (!suggestion) return;
    chatbox.value = _.get(suggestion, "item.value", "");
  }

  const text = filteredXSSMessage.value;
  if (text) {
    lastUserMessage.value = text;
    sendMessage({ text });
    chatbox.value = "";
    if (!showPushToTalk.value) {
      if (textbox.value) {
        (textbox.value as any).suggestions = [];
        textbox.value.focus();
      }
    }

    // Fix iOS Autocorrect Bug.
    _.delay(() => (chatbox.value = new String() as any), 100);
  }
  selectedVideo.value = null;
}

async function confirmFileUpload(files: File[]) {
  updateTypingIndicator(true);
  sendingAudio.value = true;

  try {
    await store.uploadFile({
      files: files,
      vm: instance?.proxy,
    });
  } catch (err) {
    ElNotification({
      title: "File upload failed",
      type: "error",
    });
  } finally {
    updateTypingIndicator(false);
    sendingAudio.value = false;
    audioRecorderRef.value?.cancelAudio();
  }
}

async function endLiveChat() {
  sendPostback({
    text: "End Live Chat",
    event: "handover_stop",
    data: "user",
  });
  await store.stopLivechat();
}

async function endSession() {
  endButtonLoading.value = true;
  if (settings.value?.liveChatStatus) {
    await endLiveChat();
  } else if (postChatFormEnabled.value) {
    endChat();
  } else {
    await store.endSession();
    sessionEnded.value = true;
  }
  endButtonLoading.value = false;
}

async function startSession() {
  endButtonLoading.value = true;
  await _stop({});
  sessionEnded.value = false;
  endButtonLoading.value = false;
  await _start({ with_register: false, with_login: true });
}

function toggleVoice() {
  showPushToTalk.value = !showPushToTalk.value;

  if (showPushToTalk.value) {
    showSuggestion.value = false;
    voice.value = $listen?.((text: string, complete?: boolean) => {
      if (text) {
        chatbox.value = text.trim();
      }
    });
  } else {
    showSuggestion.value = true;
  }
}

function startListen() {
  listening.value = true;
  voice.value?.start();
}

function stopListen() {
  if (listening.value) {
    listening.value = false;
    voice.value?.stop();
    send();

    setTimeout(() => {
      chatbox.value = "";
    }, 500);
  }
}

function speak(text: string, event: MouseEvent) {
  if ((event.target as HTMLElement).tagName !== "A") {
    $speak?.(text);
  }
}

function updateOnlineStatus(e: Event) {
  isInternetConnectionAvailable.value = e.type === "online";
}

function getNotifyBoxOffset() {
  return isMobileMode.value ? 40 : 600;
}

function getNotifyBoxCustomClass() {
  return window.innerWidth < 350 ? "el-notification__small" : "";
}

function scrollToStickyMenu() {
  const language = stickyMenuLanguage.value;
  const stickyMenuEl = _.last(document.querySelectorAll(`.sticky-menu-${language}`));
  const webchatBody = document.getElementById("keyreply-panel-body");
  if (webchatBody && stickyMenuEl) {
    webchatBody.scrollTo({
      top: (stickyMenuEl as HTMLElement).offsetTop - 50,
      behavior: "smooth",
    });
  }
}

function onWidgetScroll(e: Event) {
  const target = e.target as HTMLElement;
  const widgetScrollTopPos = target.scrollTop;
  if (widgetScrollTopPos > lastScrollTop.value) {
    isShowScrollButton.value = false;
  } else {
    if (!_.isEmpty(stickyMenu.value)) {
      const language = stickyMenuLanguage.value;
      const stickyMenuElement = _.last(
        document.querySelectorAll(`.sticky-menu-${language}`)
      ) as HTMLElement;
      const scrollPosition = target.scrollTop + target.clientHeight;
      if (stickyMenuElement && scrollPosition > stickyMenuElement.offsetTop) {
        isShowScrollButton.value = !isNodeVisibleWhenScroll(stickyMenuElement, target);
      } else {
        isShowScrollButton.value = false;
      }
    }
  }
  lastScrollTop.value = widgetScrollTopPos <= 0 ? 0 : widgetScrollTopPos;
}

function isNodeVisibleWhenScroll(ele: HTMLElement, container: HTMLElement) {
  const eleTop = ele.offsetTop;
  const eleBottom = eleTop + ele.clientHeight;
  const containerTop = container.scrollTop;
  const containerBottom = containerTop + container.clientHeight;

  return (
    (eleTop >= containerTop && eleBottom <= containerBottom) ||
    (eleTop < containerTop && containerTop < eleBottom) ||
    (eleTop < containerBottom && containerBottom < eleBottom)
  );
}

function handleChangeWidgetSize(value: boolean) {
  applyWidgetSizeStyle(widgetElement.value, value);
  tall.value = value;
}

function handleTrendingTopicList(isOpen = false) {
  isOpenTrendingTopic.value = !isOpenTrendingTopic.value;
}

function handleMinimizeVideo(minimize = true) {
  isMinimizeVideo.value = minimize;
  if (minimize) {
    selectedVideo.value = null;
    nextTick(scrollToBottom);
  }
}

function handleExpandVideo(value: any) {
  selectedVideo.value = value;
  isMinimizeVideo.value = false;
}

function resetSelectedVideo() {
  selectedVideo.value = null;
  nextTick(scrollToBottom);
}

function handleTypingMessage(value: string) {
  chatbox.value = value;
}

function openRecording() {
  useAudioInput.value = true;
  chatbox.value = "";
  nextTick(() => {
    audioRecorderRef.value?.startRecording();
  });
}

function updateAudioFile(file: Blob | null) {
  audioFile.value = file;
}

function micBackAction() {
  if (audioFile.value) {
    audioRecorderRef.value?.cancelAudio();
  } else {
    useAudioInput.value = false;
  }
}

function containsMarkdown(text: string) {
  if (!text || typeof text !== "string" || text.trim() === "") {
    return false;
  }

  const tokens = marked.lexer(text);
  const commonMarkdownTokens = [
    "code",
    "heading",
    "table",
    "hr",
    "blockquote",
    "list",
    "list_item",
    "link",
    "image",
    "strong",
    "em",
    "codespan",
    "del",
    "def",
    "escape",
    "tag",
    "reflink",
    "url",
  ];
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const foundMarkdownToken = tokens.some((token) => {
    if ((token.type === "link" || token.type === "url") && emailRegex.test(text)) {
      return false;
    }
    return commonMarkdownTokens.includes(token.type);
  });

  return foundMarkdownToken;
}

// ====================
// SignalR Connection Setup (from created() hook)
// ====================

const initSignalR = async () => {
  window.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      store.clearUnreadMessageCount();
    }
  });

  const semanticUrl = settings.value?.semanticServiceUrl;
  if (!semanticUrl) {
    console.warn('SignalR: No semantic service URL configured');
    return;
  }

  const hubConnection = signalRHubConnection(semanticUrl);

  hubConnection.on("StartReceiveMessage", () => {
    tempGenerateMessageId.value = uuid();
    const newMessage = {
      data: {
        content: [{ text: "" }],
      },
      id: tempGenerateMessageId.value,
      timestamp: new Date(),
      type: "reply" as const,
    };
    tempGenerateMessage.value = newMessage;
    messages.value.push(newMessage);
    botIsGenerateAnswer.value = true;
  });

  hubConnection.on("ReceiveMessage", (message: string) => {
    if (message) {
      const currentMessage = _.find(
        messages.value,
        (o: any) => o.id === tempGenerateMessageId.value
      );
      if (currentMessage) {
        const currentMessageContent = currentMessage.data.content[0].text;
        const currentMessageIndex = _.findIndex(
          messages.value,
          (o: any) => o.id === tempGenerateMessageId.value
        );
        const newMessage = currentMessageContent.concat(message);
        currentMessage.data.content[0].text = newMessage;
        messages.value[currentMessageIndex] = currentMessage;
        nextTick(scrollToBottom);
      }
    }
  });

  hubConnection.on("GetConnectionId", (connectionId: string) => {
    store.setSignalRConnection(connectionId);
  });

  hubConnection.on("EndReceiveMessage", () => {
    botIsGenerateAnswer.value = false;
    _.delay(() => {
      textbox.value?.focus();
    }, 100);
  });

  await hubConnection.start();
  await hubConnection.invoke("GetConnectionId");
};

// Initialize SignalR (equivalent to created() hook)
initSignalR();

// ====================
// Expose for template
// ====================

// Expose $device for template usage
defineExpose({
  $device,
});

</script>

<style scoped lang="scss">
$font-stack: "ProximaNova", "Montserrat", "Avenir Next", -apple-system, BlinkMacSystemFont,
  "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
  "Segoe UI Symbol";

// CSP-compliant positioning using CSS variables
#keyreply-chat-window {
  position: fixed;

  // Use CSS variables for dynamic positioning (CSP-compliant)
  bottom: var(--widget-bottom, 0);
  width: 100%;
  height: var(--widget-height, 100%);
  right: var(--widget-right, auto);
  top: var(--widget-top, auto);
  left: var(--widget-left, auto);
  overflow: visible;
  // overflow:visible;

  background-color: white;
  // box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.15);
  box-shadow: 0 6px 12px rgba(71, 69, 123, 0.24), 0 2px 4px rgba(71, 69, 123, 0.12);
  text-rendering: optimizeLegibility;

  // border: 1px solid #eee;

  /* TODO: change this to the highest */
  z-index: 2000;
  border-radius: 10px;

  &.tall,
  &.tall #keyreply-panel-header {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .keyreply-livechat-banner {
    position: absolute;
    top: 54px;
    border-radius: 0 0 4px 4px;
    width: 100%;
    height: 50px;
    box-sizing: border-box;
    background-color: #f0f9eb;
    color: #67c23a;
    font-size: 13px;
    font-weight: bold;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-left: 5px;
    z-index: 2002;
  }

  .keyreply-livechat-banner-large {
    position: absolute;
    top: 54px;
    border-radius: 0 0 4px 4px;
    width: 100%;
    height: 50px;
    box-sizing: border-box;
    background-color: #f0f9eb;
    color: #67c23a;
    font-size: 13px;
    font-weight: bold;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-left: 5px;
  }

  .keyreply-disconnected-banner {
    position: absolute;
    top: 54px;
    border-radius: 0 0 4px 4px;
    width: 100%;
    height: 50px;
    box-sizing: border-box;
    background-color: #bfbfbf;
    color: #000000;
    font-size: 16px;
    font-weight: bold;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-left: 3px;
  }

  .keyreply-notification-banner {
    height: auto;
    text-align: center;
    font-size: 12px;
    top: 54px;
    padding: 5px;
    position: absolute;
    width: 100%;
    box-sizing: border-box;
    background-color: #bfbfbf;
    color: #000000;
    font-weight: bold;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-left: 5px;
  }

  #keyreply-notification-banner-text {
    p {
      display: inline !important;
    }
  }

  .keyreply-maintenance-banner {
    height: auto;
    text-align: center;
    font-size: 12px;
    top: 44px;
    padding: 5px;
    position: absolute;
    border-radius: 0 0 4px 4px;
    width: 100%;
    box-sizing: border-box;
    background-color: #bfbfbf;
    color: #000000;
    // left: 0;
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-left: 5px;
    margin-top: 16px;
  }

  #keyreply-maintenance-banner-text {
    p {
      display: inline !important;
    }
  }

  .keyreply-disconnected-banner-text {
    position: absolute;
    top: 16px;
    text-align: center;
    overflow: hidden;
    z-index: 9999;
  }

  .keyreply-livechat-banner-text {
    position: absolute;
    top: 16px;
    text-align: center;
    overflow: hidden;
  }

  .keyreply-livechat-end-button {
    margin: 8px;
    color: #409eff;
    background: #ecf5ff;
    border-color: #b3d8ff;
    cursor: pointer;
  }

  #keyreply-panel-header {
    user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 60px !important;
    z-index: 1;
    width: 100%;
    padding: 13px 10px 12px;
    zoom: 1;
    cursor: pointer;
    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.06), 0 2px 0 0 rgba(0, 0, 0, 0.01);
    border-radius: 10px 10px 0px 0px;
    text-align: left;

    .keyreply-chat-title {
      display: inline-block;
      position: absolute;
      margin-top: 10px;
      line-height: 100%;
      font-size: 20px;
      padding-left: 5px;

      &.keyreply-chat-logo-large {
        margin-left: 118px;
      }
    }

    .keyreply-header-button {
      cursor: pointer;
      position: absolute;
      top: 20px;
    }

    .keyreply-chat-logo {
      display: inline-block;
      height: 100%;
      max-height: 50px;
    }

    .keyreply-chat-logo-large {
      top: -55px;
      z-index: 0;
      position: absolute;
      height: 100px;
      width: 100px;
    }

    .keyreply-chat-title-large {
      position: absolute;
      margin-left: 124px;
      font-size: 27px;
    }
  }

  #keyreply-panel-body {
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    overflow-x: hidden;
    position: absolute;
    bottom: 40px;
    top: 60px;
    width: 100%;
    background-position: center;
    padding: 16px 0;
    z-index: 0;

    // CSP-compliant: use CSS variable for dynamic top positioning
    &.panel-with-top {
      top: var(--panel-top, 60px);
    }

    // CSP-compliant: use CSS variable for dynamic background
    &.panel-with-background {
      background-image: var(--panel-background, none);
    }

    .keyreply-subtle-link {
      text-align: center;
      color: rgba(0, 0, 0, 0.7);
      margin: 4px auto;
      font-size: 12px;
      cursor: pointer;
    }
  }

  #keyreply-panel-body-form {
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    overflow-x: hidden;
    position: absolute;
    bottom: 40px;
    top: 60px;
    width: 100%;
    background-position: center;
    padding: 0;
    z-index: 0;
  }

  #keyreply-panel-frame {
    overflow-x: hidden;
    position: absolute;
    top: 60px;
    bottom: 0px;
    width: 100%;
    padding: 0;
    z-index: 2;

    // CSP-compliant: use CSS variable for dynamic top positioning
    &.panel-with-top {
      top: var(--panel-top, 60px);
    }
  }

  #keyreply-panel-footer {
    display: flex;
    position: absolute;
    top: auto;
    padding: 0;
    bottom: 0px;
    left: 0px !important;
    right: 0px !important;
    z-index: 900;
    background-color: #fff;
    border-radius: 0px 0px 10px 10px;
    box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0.04), 0px -2px 0px 0px rgba(0, 0, 0, 0.01);
    z-index: 1;
    transform: translateY(0px) !important;

    .keyreply-footer-button {
      min-height: 30px;
      display: inline-block;
      padding: 10px;
      text-align: center;
      z-index: 2;
      color: grey;
      cursor: pointer;

      i {
        height: 30px;
        width: 30px;
        font-size: 24px;
      }
    }
    .keyreply-footer-button.keyreply-mic-button {
      color: #e4392b;
      position: absolute;
      right: 16px;
    }
    .keyreply-footer-button.keyreply-mic-back-button {
      color: #e4392b;
      margin-left: 0px !important;
    }

    .keyreply-footer-button.keyreply-send-button {
      align-items: center;
      display: flex;
      flex: 0 0 36px;
      color: #e4392b;
      transition: transform 0.35s cubic-bezier(0.23, 1, 0.32, 1);
      transform: scale(0);

      &.visible {
        transform: scale(1);
      }
      &.disabled {
        color: grey;
        pointer-events: none;
      }
    }

    .keyreply-autocomplete-popper {
      z-index: 1000 !important;
      font-family: $font-stack;
      font-weight: 400;
      position: absolute;
      bottom: 46px;
      background: white;
      opacity: 0.9;
      width: 85%;
    }

    .keyreply-suggestion {
      opacity: 1;
      color: #212b35;
      white-space: normal;
      word-break: break-word;
      cursor: pointer;
      opacity: 1;
      padding: 8px;

      transition: background-color 0.35s ease-in;

      &.keyreply-suggestion-selected {
        background-color: rgb(220, 220, 220);
      }
    }
  }

  #keyreply-scroll-button {
    position: absolute;
    right: 25px;
    bottom: 60px;
    width: 55px;
    height: 50px;
    min-width: 0;

    .circle-button {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      min-width: 0;
    }

    .circle-button:hover ~ .info-button {
      visibility: visible;
      opacity: 1;
    }
    .circle-button:hover {
      opacity: 0;
      visibility: hidden;
    }

    .info-button {
      position: absolute;
      top: 0;
      right: 0;
      margin: 0;
      padding: 4px;
      display: block;
      white-space: normal;
      text-align: center;
      max-width: 55px;
      width: auto;
      font-size: 12px;
      visibility: hidden;
      opacity: 0;
      min-width: 0;

      p {
        margin: 0;
      }

      &:hover {
        visibility: visible;
        opacity: 1;
      }
    }
  }

  @media only screen and (min-width: 768px) and (max-width: 1024px) {
    right: 0 !important;
    bottom: 0 !important;
  }

  @media only screen and (max-height: 650px) {
    height: 100% !important;
  }
}

@media only screen and (max-height: 660px) {
  #keyreply-panel-header .keyreply-chat-logo-large {
    display: inline-block;
    height: 44px !important;
    width: 44px !important;
    top: -5px !important;
  }
}

@media only screen and (max-width: 768px) {
  #keyreply-chat-window {
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }

  #keyreply-panel-header {
    .keyreply-chat-logo-large {
      display: inline-block;
      height: 44px !important;
      width: 44px !important;
      top: -5px !important;
    }

    .keyreply-chat-title-large {
      margin-left: 55px !important;
      font-size: 20px !important;
    }
  }
}

@media only screen and (min-width: 1024px) {
  #keyreply-chat-window {
    width: 410px;
    height: 640px;
    bottom: 10px;

    &.wide {
      width: 550px;
    }

    &.full {
      top: 0 !important;
      left: 0 !important;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
    }

    transition: height 0.3s, right 0.3s, bottom 0.3s;
  }

  #keyreply-chat-window.iossdk {
    margin-right: 0;
  }

  #keyreply-container.keyreply-left #keyreply-chat-window {
    right: unset;
    bottom: 10px;
    left: 20px;
  }
}

@media only print {
  #keyreply-chat-window {
    display: none !important;
  }
}

.floating-mobile-menu-icon:hover {
  fill: #fff;
}

.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2001;
}

.lightbox .lightbox-close-button {
  position: absolute;
  top: 5px;
  right: 20px;
  color: white;
  font-size: 50px;
  cursor: pointer;
}

.full-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 0;
}

.image-lightbox {
  z-index: 1;
}
</style>
<style lang="scss">
$font-stack: "ProximaNova", "Montserrat", "Avenir Next", -apple-system, BlinkMacSystemFont,
  "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
  "Segoe UI Symbol";

.el-notification__small {
  width: 310px !important;
  right: 6px !important;
}

.el-tabs__header {
  margin-bottom: 0 !important;
}

#keyreply-chat-window.el-tabs__item.is-top {
  color: white;
}

.keyreply-message-input textarea {
  min-height: 38px !important;
  border: none !important;
  resize: none;
  border-radius: 0px;
  padding: 5px 5px;
  box-sizing: border-box;
  box-shadow: none !important;
}

.keyreply-message-input textarea:focus {
  border: none !important;
  box-shadow: none !important;
}

textarea {
  font: inherit;
}

.el-textarea__inner {
  font-size: 16px !important;
}

.keyreply-message-input {
  flex: 1;
  width: 100%;
  -webkit-writing-mode: horizontal-tb !important;
  resize: none;
  overflow: hidden !important;
  clear: both;
  text-rendering: auto;
  letter-spacing: normal;
  word-spacing: normal;
  text-transform: none;
  text-indent: 0px;
  text-shadow: none;
  display: inline-block;
  text-align: start;
  cursor: text;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  -webkit-rtl-ordering: logical;
  flex-direction: column;
  outline-offset: -2px;

  border: 1px solid #fff;
  // padding: 0;
  font: inherit !important;
  border-radius: 4px;

  background-color: #fff;
  color: #212121;
  outline: 0;

  -webkit-appearance: none;
  overflow: hidden;

  // padding-left: 47px;
  width: 314px;
  height: auto;
  // padding-right: 49px;
  box-sizing: border-box;

  // border-top: 13px solid #fff;
  // border: 1px solid #77bbff;
  transition: all 0.35s ease-out;

  max-height: 200px !important;
  padding: 4px 0px;
  margin-left: 10px;

  // CSP-compliant border radius classes (instead of inline style)
  &.chatbox-radius-small :deep(textarea) {
    border-radius: 10px;
  }

  &.chatbox-radius-large :deep(textarea) {
    border-radius: 50px;
  }
}

// CSP-compliant tab label styling using CSS variables
.tab-label-styled {
  color: var(--tab-color, inherit);
  font-size: var(--tab-font-size, inherit);
  font-weight: var(--tab-font-weight, inherit);
  background-color: var(--tab-background-color, transparent);
}

::-webkit-input-placeholder {
  /* Edge */
  color: #b1bbc7;
  font-size: 16px;
}

:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  color: #b1bbc7;
  font-size: 16px;
}

::placeholder {
  color: #b1bbc7;
  font-size: 16px;
}

.keyreply-email-dialog {
  position: absolute !important;
  left: 0px !important;
  top: 0px !important;
}
.keyreply-video {
  padding: 0px !important;
  overflow: hidden !important;
  bottom: 0px !important;
}
</style>

<style scoped>
.bounce-enter-active {
  animation: bounce-in 0.2s;
}
.bounce-leave-active {
  animation: bounce-in 0.2s reverse;
}
@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(0.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>

<style lang="scss">
.ui-beta {
  #keyreply-chat-window {
    box-shadow: 0 4px 8px 0 rgba(51, 51, 51, 0.2);
    border-radius: 16px;

    &.tall #keyreply-panel-footer {
      border-radius: 0;
    }

    &.tall #keyreply-header-menu {
      border-radius: 0;
    }

    .keyreply-disconnected-banner {
      top: 60px;
      height: 32px;
      background-color: #ffcccc;
      color: #cc0000;
      font-size: 12px;
    }

    .keyreply-typing-banner {
      position: absolute;
      top: 60px;
      border-radius: 0 0 4px 4px;
      width: 100%;
      height: 32px;
      box-sizing: border-box;
      font-size: 12px;
      font-weight: bold;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: space-around;
      padding-left: 3px;
      background: #dce2f5;
      color: #a6b5e6;
    }

    #keyreply-header-menu {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #0000008c;
      border-radius: 16px;
      z-index: 99;
      .keyreply-header-menu-container {
        position: absolute;
        right: 16px;
        left: 16px;
        top: 96px;
        padding: 24px 16px 16px 16px;
        background: #fff;
        border-radius: 16px;
      }

      .keyreply-header-menu-section {
        background: var(--keyreply-user-main-bg-color);
        border-radius: 8px;
        padding: 16px;

        &:nth-child(2) {
          margin: 16px 0;
        }

        .keyreply-header-menu-section-tittle {
          font-size: 16px;
          font-weight: 900;
          color: var(--keyreply-user-main-title-color);
        }

        .keyreply-header-menu-button-group {
          display: flex;
          margin-top: 16px;

          .keyreply-header-button-item {
            display: flex;
            align-items: center;
            width: 50%;
            cursor: pointer;

            .keyreply-header-button {
              height: 32px;
              width: 32px;
              display: flex;
              justify-content: center;
              align-items: center;
              text-align: center;
              color: var(--keyreply-widget-icon-text-color);
              background: var(--keyreply-widget-icon-bg-color);
              border-radius: 200px;
              z-index: 2;

              i {
                font-size: 16px;
              }
            }

            span {
              font-size: 16px;
            }
          }
        }
      }

      .keyreply-header-powered {
        margin-top: 8px;
        text-align: center;
        font-size: 16px;
        color: #999999;
      }
    }

    #keyreply-panel-header {
      cursor: grab;
      border-radius: 16px 16px 0px 0px;

      .keyreply-chat-title {
        font-size: 16px;
      }

      .keyreply-header-button {
        top: 50%;
        transform: translateY(-45%);
        height: 32px;
      }

      @media only screen and (min-width: 768px) and (max-width: 1024px) {
        cursor: default !important;
      }
    }

    #keyreply-panel-body {
      bottom: 60px;

      .keyreply-powered {
        font-size: 16px;
        color: #999999;
      }
    }

    #keyreply-panel-footer {
      align-items: center;
      padding: 16px !important;
      background-color: #eee;
      border-radius: 0px 0px 16px 16px;
      min-height: 80px;
      transform: translateY(0px) !important;

      .keyreply-footer-button {
        min-height: 32px;
        min-width: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #fff;
        border-radius: 200px;
        z-index: 2;
        padding: 2px;

        i {
          font-size: 24px;
          width: auto;
          height: auto;
        }
      }

      .keyreply-footer-button-icon {
        background-color: var(--keyreply-widget-icon-bg-color);
        color: var(--keyreply-widget-icon-text-color);
      }
    }

    .keyreply-dropdown-icon {
      color: var(--keyreply-header-font-color);
    }

    .keyreply-hamburger-line {
      background-color: var(--keyreply-header-modal-icon-color);
    }

    @media only screen and (min-width: 768px) and (max-width: 1024px) {
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
    }

    @media only screen and (max-height: 650px) {
      height: 100% !important;
    }

    .keyreply-message-input textarea {
      padding: 7px 16px 6px 16px;
      font-size: 16px;
    }

    .keyreply-message-input textarea::placeholder {
      font-size: 16px;
      color: #ccc;
    }

    .el-textarea__inner {
      font-size: 16px !important;
    }

    ::-webkit-input-placeholder {
      /* Edge */
      color: #b1bbc7;
      font-size: 16px;
    }

    :-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: #b1bbc7;
      font-size: 16px;
    }

    ::placeholder {
      color: #b1bbc7;
      font-size: 16px;
    }

    .keyreply-email-dialog {
      position: absolute !important;
      top: 0px !important;
    }

    .keyreply-message-input {
      order: -1;
      border-radius: 50px;
      margin-left: 0;
      min-height: 32px;
    }
  }

  #keyreply-scroll-button {
    bottom: 85px !important;
  }
}
</style>

<style lang="scss" scoped>
// Header themed with CSS variables from theme-variables.ts
.header-themed {
  background-color: var(--keyreply-header-bg-color);
  color: var(--keyreply-header-font-color, white);
  border-bottom: 5px solid var(--keyreply-header-border-color);
}

[dir="ltr"] #keyreply-chat-window {
  .keyreply-livechat-banner {
    left: 0;
    padding-left: 5px;
  }

  .keyreply-livechat-banner-large {
    left: 0;
    padding-left: 5px;
  }

  .keyreply-disconnected-banner {
    left: 0;
    padding-left: 3px;
  }

  .keyreply-notification-banner {
    left: 0;
    padding-left: 5px;
  }

  .keyreply-maintenance-banner {
    left: 0;
    padding-left: 5px;
  }

  #keyreply-header-menu {
    .keyreply-header-menu-section {
      .keyreply-header-menu-button-group {
        .keyreply-header-button-item {
          .keyreply-header-button {
            margin-right: 16px;
          }
        }
      }
    }
  }

  #keyreply-panel-header {
    text-align: left;

    .keyreply-chat-title {
      padding-left: 5px;

      &.keyreply-chat-logo-large {
        margin-left: 118px;
      }
    }

    .keyreply-header-button {
      right: 17px;
    }

    .keyreply-chat-title-large {
      margin-left: 124px;
    }
  }

  #keyreply-panel-body {
    .keyreply-powered {
      text-align: right;
      margin-right: 16px;
    }
    .keyreply-powered.mobile-mode {
      text-align: center;
    }
  }

  #keyreply-panel-footer {
    .keyreply-footer-button {
      margin-left: 8px;
    }
  }
}

[dir="rtl"] #keyreply-chat-window {
  .keyreply-livechat-banner {
    right: 0;
    padding-right: 5px;
  }

  .keyreply-livechat-banner-large {
    right: 0;
    padding-right: 5px;
  }

  .keyreply-disconnected-banner {
    right: 0;
    padding-right: 3px;
  }

  .keyreply-notification-banner {
    right: 0;
    padding-right: 5px;
  }

  .keyreply-maintenance-banner {
    right: 0;
    padding-right: 5px;
  }

  #keyreply-header-menu {
    .keyreply-header-menu-section {
      .keyreply-header-menu-button-group {
        .keyreply-header-button-item {
          .keyreply-header-button {
            margin-left: 16px;
          }
        }
      }
    }
  }

  #keyreply-panel-header {
    text-align: right;

    .keyreply-chat-title {
      padding-right: 5px;

      &.keyreply-chat-logo-large {
        margin-right: 118px;
      }
    }

    .keyreply-header-button {
      left: 17px;
    }

    .keyreply-chat-title-large {
      margin-right: 124px;
    }
  }

  #keyreply-panel-body {
    .keyreply-powered {
      text-align: left;
      margin-left: 16px;
    }
    .keyreply-powered.mobile-mode {
      text-align: center;
    }
  }

  #keyreply-panel-footer {
    .keyreply-footer-button {
      margin-right: 16px;
    }
  }
}

@media only screen and (max-height: 660px) {
  #keyreply-panel-header {
    cursor: default !important;
  }
}

@media only screen and (max-width: 768px) {
  #keyreply-panel-header {
    cursor: default !important;
  }

  [dir="ltr"] #keyreply-panel-header {
    .keyreply-chat-title-large {
      margin-left: 55px !important;
    }
  }

  [dir="rtl"] #keyreply-panel-header {
    .keyreply-chat-title-large {
      margin-right: 55px !important;
    }
  }
}

@media only screen and (min-width: 1024px) {
  [dir="ltr"] #keyreply-chat-window {
    transition: height 0.3s, right 0.3s, bottom 0.3s;
  }

  [dir="rtl"] #keyreply-chat-window {
    transition: height 0.3s, left 0.3s, bottom 0.3s;
  }

  [dir="ltr"] #keyreply-chat-window.iossdk {
    margin-right: 0;
  }

  [dir="rtl"] #keyreply-chat-window.iossdk {
    margin-left: 0;
  }

  #keyreply-container.keyreply-left #keyreply-chat-window {
    bottom: 10px;
  }

  [dir="ltr"] #keyreply-container.keyreply-left #keyreply-chat-window {
    right: unset;
    left: 20px;
  }

  [dir="rtl"] #keyreply-container.keyreply-left #keyreply-chat-window {
    left: unset;
    right: 20px;
  }
}

[dir="ltr"] .lightbox {
  left: 0;
}

[dir="rtl"] .lightbox {
  right: 0;
}

[dir="ltr"] .el-notification__small {
  right: 6px !important;
}

[dir="rtl"] .el-notification__small {
  left: 6px !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss" scoped>
$dot-width: 5px;
$dot-color: #999999;
$speed: 1.5s;

.typing {
  position: relative;
  height: fit-content;
  margin: 10px 5px;

  span {
    content: "";
    animation: blink $speed infinite;
    animation-fill-mode: both;
    height: $dot-width;
    width: $dot-width;
    background: $dot-color;
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 50%;

    &:nth-child(2) {
      animation-delay: 0.2s;
      margin-left: $dot-width * 1.5;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
      margin-left: $dot-width * 3;
    }
  }
}

@keyframes blink {
  0% {
    opacity: 0.1;
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0.1;
  }
}

.keyreply-maintenance-banner-close {
  position: absolute;
  top: 0px;
  right: 5px;
  cursor: pointer;
  color: red !important;
  font-size: 16px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
}
</style>
